import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Play, Pause, X } from "lucide-react";
import { Button } from "../ui/button";
import { RecordingData } from "../../hooks/useMediaRecorder";
import { cn } from "../../lib/utils";

interface MediaNotePlayerProps {
  recording: RecordingData;
  type: "audio" | "video";
  label?: string;
  autoPlay?: boolean;
  /** Unique ID for this media note (to track auto-play state) */
  itemId: string;
  /** Called when auto-play has been triggered (to track single-play state) */
  onAutoPlayed?: (itemId: string) => void;
  /** Set of item IDs that have already auto-played */
  autoPlayedItems?: Set<string>;
}

export function MediaNotePlayer({
  recording,
  type,
  label,
  autoPlay = false,
  itemId,
  onAutoPlayed,
  autoPlayedItems,
}: MediaNotePlayerProps) {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [showPlayer, setShowPlayer] = useState(false);

  // Check if already auto-played (from parent tracking)
  const alreadyAutoPlayed = autoPlayedItems?.has(itemId) || false;

  // Tracks whether THIS mount has already succeeded at autoplaying, so the
  // effect doesn't re-attempt playback if it re-runs for an unrelated
  // dependency change (e.g. alreadyAutoPlayed flipping true right after we
  // report it), and so a stale/superseded element's cleanup knows whether
  // its own play() actually landed before it gets torn down.
  const playSucceededRef = useRef(false);

  // Auto-play once the element has buffered enough to actually play through,
  // not just as soon as it mounts (which used to start audio before the
  // video had loaded, and could race with an unmounted/stale instance).
  useEffect(() => {
    // Each effect run (i.e. each mounted instance / each dependency change)
    // gets its own cancellation flag, closed over by attemptPlay below —
    // this avoids the previous shared-ref bug where a fast remount could
    // mark a still-in-flight attempt as "already handled" before its
    // canplay/play() actually resolved, letting it play on undetected.
    let cancelled = false;

    if (!(autoPlay && !playSucceededRef.current && !alreadyAutoPlayed && recording.url)) {
      return;
    }

    const element = type === "audio" ? audioRef.current : videoRef.current;
    if (!element) return;

    const attemptPlay = () => {
      if (cancelled) return;
      setShowPlayer(true);

      element
        .play()
        .then(() => {
          if (cancelled) {
            // This effect run was superseded (unmount, or a dependency
            // changed) before play() resolved — undo it immediately so no
            // orphaned audio/video survives on a detached element.
            element.pause();
            element.currentTime = 0;
            return;
          }
          playSucceededRef.current = true;
          setIsPlaying(true);
          onAutoPlayed?.(itemId);
        })
        .catch((error) => {
          if (cancelled) return;
          console.warn(`${type} autoplay failed:`, error);
          // Show player anyway so user can manually play
        });
    };

    // readyState >= 3 (HAVE_FUTURE_DATA) means playback can start smoothly.
    if (element.readyState >= 3) {
      attemptPlay();
    } else {
      element.addEventListener("canplay", attemptPlay, { once: true });
    }

    return () => {
      cancelled = true;
      element.removeEventListener("canplay", attemptPlay);
      // Only tear down playback that THIS run started but never confirmed
      // success on — once playSucceededRef is set, a re-run from an
      // unrelated dep must not pause a video that's genuinely playing; a
      // real unmount discards the element regardless.
      if (!playSucceededRef.current && !element.paused) {
        element.pause();
        element.currentTime = 0;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onAutoPlayed is
    // an unstable inline callback from the parent; including it would re-run
    // (and tear down via cleanup) this effect right after playback starts.
  }, [autoPlay, alreadyAutoPlayed, recording.url, type, itemId]);

  const handlePlayPause = () => {
    const element = type === "audio" ? audioRef.current : videoRef.current;
    if (!element) return;

    if (!element.paused) {
      element.pause();
      setIsPlaying(false);
    } else {
      setShowPlayer(true);
      element
        .play()
        .then(() => setIsPlaying(!element.paused))
        .catch(console.error);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
  };

  const handleClose = () => {
    const element = type === "audio" ? audioRef.current : videoRef.current;
    if (element) {
      element.pause();
      element.currentTime = 0;
      // Authoritative: only report "stopped" once the element confirms it.
      setIsPlaying(!element.paused);
    } else {
      setIsPlaying(false);
    }
    setShowPlayer(false);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="my-2">
      {/* Hidden audio element */}
      {type === "audio" && (
        <audio
          ref={audioRef}
          src={recording.url}
          onEnded={handleEnded}
          preload={autoPlay ? "auto" : "metadata"}
        />
      )}

      {/* Label */}
      {label && (
        <p className="mb-2 text-sm font-medium text-muted-foreground">
          {label}
        </p>
      )}

      {/* Player UI */}
      {type === "video" ? (
        // Video player
        <div className="relative overflow-hidden bg-black border rounded-lg">
          <video
            ref={videoRef}
            src={recording.url}
            onEnded={handleEnded}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            controls={showPlayer}
            controlsList="nodownload"
            className="w-full aspect-video"
            preload={autoPlay ? "auto" : "metadata"}
            poster=""
          />
          {!showPlayer && (
            <div
              className="absolute inset-0 flex items-center justify-center cursor-pointer bg-black/40"
              onClick={handlePlayPause}
            >
              <div className="flex items-center gap-2 px-4 py-2 text-white rounded-full bg-white/20 backdrop-blur-sm">
                <Play className="w-5 h-5" />
              </div>
            </div>
          )}
          {showPlayer && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="absolute z-10 w-6 h-6 p-0 text-white top-1 right-1 bg-black/50 hover:bg-black/70"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      ) : (
        // Audio player
        <div className="flex items-center gap-3 p-3 border rounded-lg bg-muted/20">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePlayPause}
            className={cn(
              "w-10 h-10 p-0 rounded-full shrink-0",
              isPlaying && "bg-primary/5 border-primary/30",
            )}
          >
            {isPlaying ? (
              <Pause className="w-4 h-4 text-primary" />
            ) : (
              <Play className="w-4 h-4 text-primary ml-0.5" />
            )}
          </Button>
          <div className="flex-1">
            <span className="text-sm text-muted-foreground">
              {isPlaying
                ? t("playing", "Playing...")
                : formatTime(recording.duration)}
            </span>
          </div>
          {isPlaying && (
            <span className="flex w-2 h-2">
              <span className="absolute inline-flex w-2 h-2 bg-primary/60 rounded-full opacity-75 animate-ping" />
              <span className="relative inline-flex w-2 h-2 bg-primary rounded-full" />
            </span>
          )}
        </div>
      )}
    </div>
  );
}
